import { action, observable } from "mobx";
import BaseStore from "../../common/BaseStore";
import { WBSStore } from "modules/WBS/WBSStore";
import { WBSService } from "modules/WBS/WBSService";
import { CustomPropertyModel } from "./CustomPropertyModel";
import { CustomPropertyService } from "./CustomPropertyService";
import { RowCustomPropertyService } from "./RowCustomPropertyService";
import { FirebaseDocument } from "common/models/FirebaseDocument";
import { IRowCustomProperty } from "common/interfaces";

class Store extends BaseStore {
  @observable reloading: boolean = false;

  private WBSCustomProperties: FirebaseDocument<CustomPropertyModel>[];
  private _selectedCustomProperties: IRowCustomProperty[];

  constructor() {
    super("CustomPropertyStore");
    this.reset();
  }

  reset() {
    this._selectedCustomProperties = [];
    this.WBSCustomProperties = [];
  }

  getSelectedRowCustomProperties() {
    return [...this._selectedCustomProperties];
  }

  getWBSCustomProperties() {
    return [...this.WBSCustomProperties];
  }

  getWBSCustomPropertyByLabel(
    label: string
  ): Promise<FirebaseDocument<CustomPropertyModel> | undefined> {
    return CustomPropertyService.getWBSCustomPropertyByLabel(
      WBSStore.WBSRef,
      label
    );
  }

  @action
  setWBSCustomProperties(
    customProperties: FirebaseDocument<CustomPropertyModel>[]
  ) {
    this.WBSCustomProperties = customProperties;
  }

  @action
  setRowCustomProperties(index: number, fromResource: boolean) {
    if (!this.WBSCustomProperties) return;

    const row = this.getRowByIndex(index, fromResource);
    const mergedRowsProps = CustomPropertyService.mergePropertiesAndValues(
      this.WBSCustomProperties,
      row && row.data.customProperties
    );
    this._selectedCustomProperties = [...mergedRowsProps];
  }

  @action
  setReloadToFinished() {
    if (this.reloading) this.reloading = false;
  }

  hasDiagramAndTask() {
    const propDiagrama = CustomPropertyService.findWBSPropertyByLabel(
      this.WBSCustomProperties,
      "diagrama"
    );
    const propTarefa = CustomPropertyService.findWBSPropertyByLabel(
      this.WBSCustomProperties,
      "tarefa"
    );

    return (
      propDiagrama &&
      propTarefa &&
      WBSStore.WBSRows.some((row) => {
        if (!row.data.customProperties) {
          return false;
        }

        return (
          row.data.rowType === 0 &&
          row.data.customProperties[propDiagrama.id] &&
          row.data.customProperties[propTarefa.id]
        );
      })
    );
  }

  async getOrAddCustomProperty(label: string): Promise<string> {
    const groupProp = CustomPropertyService.findWBSPropertyByLabel(
      this.WBSCustomProperties,
      label
    );
    if (groupProp) return groupProp.id;

    const ref = WBSStore.WBSRef;
    const doc = await CustomPropertyService.createWBSCustomProperty(
      ref,
      new CustomPropertyModel(label)
    );
    if (!doc) return "";

    const properties = [...this.WBSCustomProperties];
    properties.push(doc);
    this.setWBSCustomProperties(properties);
    return doc.id;
  }

  @action
  addBlankCustomProperty() {
    const customProperties = RowCustomPropertyService.addBlankRowProperty(
      this._selectedCustomProperties
    );
    this._selectedCustomProperties = [...customProperties];
  }

  @action
  updateCustomPropertyLabel(id: string, label: string) {
    const customProperties = RowCustomPropertyService.setRowCustomPropertyLabel(
      this._selectedCustomProperties,
      id,
      label
    );
    this._selectedCustomProperties = [...customProperties];
  }

  @action
  updateCustomPropertyValue(id: string, value: string) {
    const customProperties = RowCustomPropertyService.setRowCustomPropertyValue(
      this._selectedCustomProperties,
      id,
      value
    );
    this._selectedCustomProperties = [...customProperties];
  }

  @action
  deleteCustomProperty(id: string) {
    const customProperties = RowCustomPropertyService.removeRowProperty(
      this._selectedCustomProperties,
      id
    );
    this._selectedCustomProperties = [...customProperties];
  }

  /**
   Esta função sincroniza as modificações feitas localmente nas propriedades customizadas
   da linha selecionada para o projeto
  */
  @action
  async syncCustomProperties(index: number, fromResource: boolean) {
    const rowPropsToUpdate = [...this._selectedCustomProperties];
    const result = await CustomPropertyService.syncCustomProperties(
      WBSStore.WBSRef,
      rowPropsToUpdate
    );

    const ids = result.deletedPropsIds;
    const newPropertiesList = result.updatedProps;

    await this.updateSelectedRowCustomProperty(
      index,
      fromResource,
      rowPropsToUpdate
    );

    return this.deleteCustomPropertiesFromRows(ids).then(() => {
      this.setWBSCustomProperties(newPropertiesList);
      this.setRowCustomProperties(index, fromResource);
    });
  }

  @action
  async undoSyncCustomProperty(
    index: number,
    fromResource: boolean,
    customProperties: any
  ) {
    if (!customProperties) return;
    this._selectedCustomProperties = [...customProperties];
    return CustomPropertyStore.syncCustomProperties(index, fromResource);
  }

  private async updateSelectedRowCustomProperty(
    index: number,
    fromResource: boolean,
    properties: IRowCustomProperty[]
  ) {
    let customProperties = {};
    properties.forEach((prop) => {
      if (prop.id && prop.value) customProperties[prop.id] = prop.value;
    });

    const row = this.getRowByIndex(index, fromResource);
    if (!row) return;

    await WBSStore.updateWBSSubcollection(row.id, row.data.contentId, {
      customProperties,
    });
  }

  private async deleteCustomPropertiesFromRows(idsProps: string[]) {
    if (!idsProps || !idsProps.length) return;
    const actions = Array();

    WBSStore.WBSRows.forEach((row) => {
      if (!row.data.customProperties) return;
      let needUpdate = false;

      // Remove as propriedades deletadas
      let customProperties = { ...row.data.customProperties };
      for (const key of Object.keys(customProperties)) {
        if (idsProps.includes(key)) {
          delete customProperties[key];
          needUpdate = true;
        }
      }

      if (!needUpdate) return;

      // Monta o objeto que será atualizado no firebase
      let updatedProps = Object.keys(customProperties).length
        ? { customProperties }
        : { customProperties: WBSService.getFirebaseDeletedField() }; //Deleta a propriedade se o objeto estiver vazio

      const rowRef = WBSService.getRowRef(WBSStore.WBSRef, row.id);
      actions.push({
        type: "update",
        args: [rowRef, updatedProps],
        arrayChange: () => {
          WBSStore.changeAllArray({ customProperties }, row);
        },
      });
    });

    if (actions.length) {
      await WBSStore.batchActions(actions, false);
    }
  }

  private getRowByIndex(index: number, fromResource: boolean): any | undefined {
    if (fromResource) {
      const resource = WBSStore.getResourceLineByIndex(index);
      if (!resource) return;

      const rowsList = WBSStore.getListByContent(
        resource.contentId,
        resource.id
      );

      return rowsList[0];
    }

    return WBSStore.getWBSRowLineByIndex(index);
  }
}

export const CustomPropertyStore = new Store();



// WEBPACK FOOTER //
// ./src/modules/customProperties/CustomPropertyStore.ts